import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Activity, Database, FileText, FileCheck2, AlertTriangle, CheckCircle2, Circle } from "lucide-react";
import { endpoints } from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BaseVoucherCard } from "@/components/BaseVoucherCard";
import { Badge } from "@/components/ui/badge";
import { CenteredLoader, ErrorState } from "@/components/ui/feedback";
import { formatDate } from "@/lib/utils";
import { useAuth } from "@/lib/AuthProvider";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { getUserPreferences } from "@/components/PreferenzeOperative";
import { useCerAttiviList } from "@/lib/cerAttiviChoices";

const CHECKLIST = [
  { id: "p12", label: "Configura certificato P12", to: "/impostazioni" },
  { id: "cer", label: "Aggiungi CER attivi", to: "/cer-attivi" },
  { id: "contacts", label: "Inserisci operatori in rubrica", to: "/contacts" },
  { id: "movimento", label: "Apri registro e registra un movimento", to: "/registri" },
  { id: "invio", label: "Invia registro a RENTRI", to: "/registri" },
] as const;

export function Dashboard() {
  const auth = useAuth();
  const ctx = useOperatoreContext();
  const prefs = getUserPreferences();
  const health = useQuery({ queryKey: ["health"], queryFn: endpoints.health, refetchInterval: 15_000 });
  const metrics = useQuery({ queryKey: ["metrics"], queryFn: endpoints.metrics, refetchInterval: 10_000 });
  const jobs = useQuery({ queryKey: ["jobs"], queryFn: endpoints.jobs, refetchInterval: 5_000 });
  const certs = useQuery({ queryKey: ["certs-home"], queryFn: endpoints.certificates.list });
  const contactsQ = useQuery({
    queryKey: ["contacts", "home"],
    queryFn: () => endpoints.contacts.list(),
    enabled: auth.ready && auth.authenticated,
    staleTime: 30_000,
  });
  const cerQ = useCerAttiviList();
  const movBozzeQ = useQuery({
    queryKey: ["mov-bozze-home", ctx.registroId],
    queryFn: () => endpoints.registri.listMovimentiBozza(ctx.registroId),
    enabled: !!ctx.registroId,
  });
  const giacenzeQ = useQuery({
    queryKey: ["giacenze-home", ctx.registroId],
    queryFn: () => endpoints.giacenze.perRegistro(ctx.registroId),
    enabled: !!ctx.registroId && prefs.dashboard_alert_giacenze !== false,
  });

  const bozzeAperte =
    (metrics.data?.drafts_by_status.draft ?? 0) + (metrics.data?.drafts_by_status.ready ?? 0);
  const jobPending = metrics.data?.jobs_by_status.pending ?? 0;
  const cerCount = cerQ.data?.items?.length ?? 0;
  const contactCount = contactsQ.data?.length ?? 0;

  const isStepDone = (stepId: (typeof CHECKLIST)[number]["id"]) => {
    switch (stepId) {
      case "p12":
        return (certs.data?.length ?? 0) > 0;
      case "cer":
        return cerCount > 0;
      case "contacts":
        return contactCount > 0;
      case "movimento":
        return !!ctx.registroId;
      case "invio":
        return false;
      default:
        return false;
    }
  };
  const movBozzePending =
    movBozzeQ.data?.filter((d) => d.status === "draft" || d.status === "ready").length ?? 0;
  const giacenzeAlert =
    giacenzeQ.data?.rows.filter((r) => r.giacenza_kg >= 10_000 || r.giacenza_kg < 0).length ?? 0;
  const showAlerts =
    bozzeAperte > 0 ||
    jobPending > 0 ||
    movBozzePending > 0 ||
    cerCount === 0 ||
    giacenzeAlert > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Home</h1>
          <p className="text-sm text-muted-foreground">
            Centro operativo RENTRI
            {ctx.denominazione ? ` — ${ctx.denominazione}` : ""}
          </p>
        </div>
        {health.data && (
          <Badge variant={health.data.status === "ok" ? "success" : "destructive"} className="text-xs">
            {health.data.status === "ok" ? "Sistema operativo" : "Sistema degradato"}
          </Badge>
        )}
      </div>

      {showAlerts && (
        <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-900 dark:bg-amber-950/20">
          <CardContent className="flex flex-col gap-2 p-4 text-sm">
            <div className="flex items-center gap-2 font-medium">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              Attività in sospeso
            </div>
            <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
              {cerCount === 0 && !cerQ.isLoading && (
                <li>
                  Nessun CER attivo —{" "}
                  <Link to="/cer-attivi" className="underline text-foreground">
                    configura CER attivi
                  </Link>
                </li>
              )}
              {movBozzePending > 0 && (
                <li>
                  <strong>{movBozzePending}</strong> movimenti in bozza sul registro —{" "}
                  <Link to="/registri" className="underline text-foreground">
                    Registro
                  </Link>
                </li>
              )}
              {bozzeAperte > 0 && (
                <li>
                  <strong>{bozzeAperte}</strong> bozze FIR/documenti —{" "}
                  <Link to="/drafts" className="underline text-foreground">
                    Bozze
                  </Link>
                </li>
              )}
              {jobPending > 0 && (
                <li>
                  <strong>{jobPending}</strong> job in coda —{" "}
                  <Link to="/jobs" className="underline text-foreground">
                    Job RENTRI
                  </Link>
                </li>
              )}
              {giacenzeAlert > 0 && (
                <li>
                  <strong>{giacenzeAlert}</strong> codici EER con giacenza da verificare —{" "}
                  <Link to="/giacenze" className="underline text-foreground">
                    Giacenze
                  </Link>
                </li>
              )}
            </ul>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle id="primi-passi" className="scroll-mt-24 text-base">
            Primi passi
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {CHECKLIST.map((step) => {
            const done = isStepDone(step.id);
            const Icon = done ? CheckCircle2 : Circle;
            return (
              <Link
                key={step.id}
                to={step.to}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent"
              >
                <Icon className={done ? "h-4 w-4 text-emerald-600" : "h-4 w-4 text-muted-foreground"} />
                {step.label}
              </Link>
            );
          })}
        </CardContent>
      </Card>

      <BaseVoucherCard compact id="rentri-base" />

      {/* KPI cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          title="Job pending"
          value={metrics.data?.jobs_by_status.pending ?? 0}
          icon={FileCheck2}
          accent="warning"
        />
        <KpiCard
          title="Job completati"
          value={metrics.data?.jobs_by_status.done ?? 0}
          icon={FileCheck2}
          accent="success"
        />
        <KpiCard
          title="Job falliti"
          value={(metrics.data?.jobs_by_status.failed ?? 0) + (metrics.data?.jobs_by_status.expired ?? 0)}
          icon={AlertTriangle}
          accent="destructive"
        />
        <KpiCard
          title="Bozze in corso"
          value={(metrics.data?.drafts_by_status.draft ?? 0) + (metrics.data?.drafts_by_status.ready ?? 0)}
          icon={Database}
          accent="primary"
        />
      </div>

      {/* Recent jobs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Activity className="h-4 w-4" /> Job recenti</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.isLoading && <CenteredLoader />}
          {jobs.error && <ErrorState error={jobs.error} />}
          {jobs.data && jobs.data.length === 0 && (
            <p className="text-sm text-muted-foreground py-2">Nessun job registrato.</p>
          )}
          {jobs.data && jobs.data.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="text-left py-2">Tipo</th><th className="text-left">TX RENTRI</th><th>Tentativi</th><th>Stato</th><th className="text-right">Aggiornato</th></tr>
                </thead>
                <tbody>
                  {jobs.data.slice(0, 8).map((j) => (
                    <tr key={j.id} className="border-t">
                      <td className="py-2 font-medium">{j.job_type}</td>
                      <td className="font-mono text-xs">{j.rentri_transaction_id ?? "—"}</td>
                      <td className="text-center">{j.attempts}</td>
                      <td><JobStatus status={j.status} /></td>
                      <td className="text-right text-muted-foreground text-xs">{formatDate(j.updated_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Top audit actions */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><FileText className="h-4 w-4" /> Azioni più frequenti</CardTitle></CardHeader>
        <CardContent>
          {metrics.data?.audit_top_actions?.length ? (
            <ul className="space-y-2">
              {metrics.data.audit_top_actions.slice(0, 8).map((a) => (
                <li key={a.action} className="flex items-center justify-between text-sm">
                  <span className="truncate font-mono text-xs text-muted-foreground">{a.action}</span>
                  <Badge variant="secondary">{a.count}</Badge>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">Nessuna attività ancora registrata.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, accent }: { title: string; value: number; icon: typeof Activity; accent: "primary" | "success" | "warning" | "destructive" }) {
  const colors = {
    primary: "bg-primary/10 text-primary",
    success: "bg-success/15 text-success",
    warning: "bg-warning/15 text-warning",
    destructive: "bg-destructive/10 text-destructive",
  }[accent];
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
          <p className="mt-1 text-3xl font-semibold tracking-tight">{value}</p>
        </div>
        <div className={`rounded-md p-2 ${colors}`}>
          <Icon className="h-5 w-5" />
        </div>
      </CardContent>
    </Card>
  );
}

function JobStatus({ status }: { status: string }) {
  const v = status === "done" ? "success"
    : status === "failed" || status === "expired" ? "destructive"
    : status === "running" ? "default" : "warning";
  return <Badge variant={v as never}>{status}</Badge>;
}
